import { useState, useEffect } from 'react';
import { message } from 'antd';
import {
  adminService,
  DealerPackage,
  CreateDealerPackageRequest,
  UpdateDealerPackageRequest,
} from '../../api/services/adminService';

export const useDealerPackages = () => {
  const [packages, setPackages] = useState<DealerPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState<DealerPackage | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const result = await adminService.getDealerPackages();
      setPackages(Array.isArray(result) ? result : []);
    } catch {
      message.error('Failed to load dealer packages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: CreateDealerPackageRequest) => {
    setSubmitting(true);
    try {
      await adminService.createDealerPackage(values);
      message.success('Dealer package created');
      setModalVisible(false);
      fetchPackages();
    } catch {
      message.error('Failed to create dealer package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: string, values: UpdateDealerPackageRequest) => {
    setSubmitting(true);
    try {
      await adminService.updateDealerPackage(id, values);
      message.success('Dealer package updated');
      setModalVisible(false);
      fetchPackages();
    } catch {
      message.error('Failed to update dealer package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminService.deleteDealerPackage(id);
      message.success('Dealer package deleted');
      fetchPackages();
    } catch {
      message.error('Failed to delete dealer package');
    }
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    setModalVisible(true);
  };

  const openEditModal = (pkg: DealerPackage) => {
    setEditingPackage(pkg);
    setModalVisible(true);
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  return {
    packages,
    loading,
    modalVisible,
    editingPackage,
    submitting,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch: fetchPackages,
  };
};
